import React, { FC, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { AuthWrapper } from '@src/components/AuthWrapper';
import { INDEX_ROUTE, LOGIN_ROUTE } from '@src/Routes/links';
import {
  LoginState,
  useLoginMutation,
  useRequestAuthCodeMutation,
  useVerifyAuthCodeMutation,
} from '@src/graphql/generated';
import { useAppDispatch } from '@src/redux';
import { fetchRefreshToken } from '@src/redux/actions/accessToken';
import { useQueryParams } from '@src/customHooks/useQueryParams';
import { setErrorFlashMessage } from '@src/redux/actions/flashMessages';
import { trackEvent } from '@src/metrics';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { bootIntercom } from '@src/intercom';
import { LoginFormKey } from './enums/loginFormKey.enum';
import { LoginStep } from './LoginStep';
import { LoginForm } from './interfaces/loginForm.interface';
import { LoginView } from './enums/views.enum';
import { VerifyAuthCode } from './VerifyAuthCode';
import { VerifyAuthCodeQueryParams } from './interfaces/verifyAuthCodeQueryParams.interface';
import './styles.scss';

export const Login: FC = () => {
  const dispatch = useAppDispatch();

  const history = useHistory();

  const { queryParams } = useQueryParams<VerifyAuthCodeQueryParams>();

  const [isRefetchingAccessToken, setIsRefetchingAccessToken] = useState<boolean>(false);

  const [activeView, setActiveView] = useState(LoginView.LOGIN);

  const [formData, setFormData] = useState<LoginForm>({
    [LoginFormKey.EMAIL]: '',
    [LoginFormKey.PASSWORD]: '',
    [LoginFormKey.CAPTCHA]: '',
  });
  const [verifyCode, setVerifyCode] = useState<string>('');

  const refetchAccessTokenAndLogin = async (): Promise<void> => {
    // Don't remove await, because it will redirect incorrect route after login.
    const accessToken = await dispatch(fetchRefreshToken());

    if (accessToken) {
      history.replace({ pathname: INDEX_ROUTE });

      await bootIntercom();
    }
  };

  const [requestAuthCode, { loading: isRequestAuthCodeLoading }] = useRequestAuthCodeMutation({
    onError: () => {
      dispatch(setErrorFlashMessage('Could not manage to request authentication code, please try again later.'));
    },
  });

  const [verifyAuthCode, { called: hasVerifyAuthCodeBeenCalled, loading: isVerifyAuthCodeLoading }] =
    useVerifyAuthCodeMutation({
      onCompleted: () => {
        refetchAccessTokenAndLogin();
      },
      onError: () => {
        dispatch(setErrorFlashMessage('Please make sure all fields are filled in correctly.'));
      },
    });

  const [login, { called: hasLoginBeenCalled, loading: isLoginLoading }] = useLoginMutation({
    onCompleted: async data => {
      if (data.login === LoginState.MAGIC_LINK_REQUIRED) {
        requestAuthCode({
          variables: {
            email: formData.email,
          },
        });

        setActiveView(LoginView.VERIFY_AUTH_CODE);

        return;
      }

      setIsRefetchingAccessToken(true);

      await refetchAccessTokenAndLogin();

      setIsRefetchingAccessToken(false);
    },
    onError: () => {
      if (queryParams.loginToken) {
        dispatch(setErrorFlashMessage('Login token is incorrect'));

        history.replace({ pathname: LOGIN_ROUTE, search: '' });
      } else {
        dispatch(setErrorFlashMessage('Email or password is incorrect'));
      }
    },
  });

  useEffect(() => {
    trackEvent({
      context: MetricContext.LOGIN_PAGE,
      interaction: MetricAction.VIEW_LOGIN_PAGE,
    });
  }, []);

  useEffect(() => {
    if (queryParams.loginToken && !hasLoginBeenCalled) {
      login({
        variables: {
          loginInput: {
            loginToken: queryParams.loginToken,
          },
        },
      });
    }

    if (queryParams.authCode && queryParams.email && !hasVerifyAuthCodeBeenCalled) {
      verifyAuthCode({
        variables: {
          code: queryParams.authCode,
          email: queryParams.email,
        },
      });
    }
  }, [queryParams]);

  const onLogin = (): void => {
    login({
      variables: {
        loginInput: {
          credentials: {
            email: formData.email,
            googleRecaptchaResponse: formData.captcha,
            password: formData.password,
          },
        },
      },
    });
  };

  const onVerify = (): void => {
    verifyAuthCode({
      variables: {
        code: verifyCode,
        email: formData.email,
      },
    });
  };

  if (queryParams.loginToken) {
    return null;
  }

  return (
    <AuthWrapper wrapperClassName="login-wrapper">
      {activeView === LoginView.LOGIN ? (
        <LoginStep
          formData={formData}
          isLoading={isLoginLoading || isRequestAuthCodeLoading || isRefetchingAccessToken}
          onLogin={onLogin}
          setFormData={(data: LoginForm) => setFormData(data)}
        />
      ) : (
        <VerifyAuthCode
          isLoading={isVerifyAuthCodeLoading}
          onVerify={onVerify}
          setVerifyCode={value => setVerifyCode(value)}
          verifyCode={verifyCode}
        />
      )}
    </AuthWrapper>
  );
};
